<script setup lang="ts">
import { type Component, computed } from 'vue';
import { type MenuSection } from '@/routers/routeMeta';
import CapabilityMyPageMenu from '@/components-capability/menu/sections/my-page-menu.vue';
import SearchMenu from '@/components/menu/search-menu/search-menu.vue';
import AdminMenu from '@/components/menu/sections/admin-menu.vue';
import GroupMenu from '@/components/menu/sections/group-menu.vue';
import InformationRetrievalSettingsMenu from '@/components/menu/sections/information-retrieval-settings-menu.vue';
import MyPageMenu from '@/components/menu/sections/my-page-menu.vue';
import NewsMenu from '@/components/menu/sections/news-menu.vue';
import OthersMenu from '@/components/menu/sections/others-menu.vue';
import SettingsMenu from '@/components/menu/sections/settings-menu.vue';
import SurveyMenu from '@/components/menu/sections/survey-menu.vue';
import {
  useIsInformationRetrievalSettings,
  useMenuSection,
} from '@/utils/composables/useMenuSection';
import { featureFlags } from '@/featureFlags';

const MENU_BY_SECTION: Record<MenuSection, Component | undefined> = {
  admin: AdminMenu,
  search: SearchMenu,
  news: NewsMenu,
  group: GroupMenu,
  my_page: MyPageMenu,
  others: OthersMenu,
  survey: SurveyMenu,
  notifications: undefined,
  settings: SettingsMenu,
};

/**
 * 販売プランでは個人の設定と管理者の設定を情報収集エージェントの設定にまとめるため、
 * 既存の管理・設定のメニューを1つに差し替える。
 * マイページは情報収集エージェントの中に残し、個人の設定を外した版に差し替える。
 *
 * ANDEV_10131_CAPABILITY の削除時は、
 * my-page-menu.vue を components-capability の版で上書きして featureFlags の分岐を消し、
 * MENU_BY_SECTION の admin・settings を InformationRetrievalSettingsMenu にする。
 */
const section = useMenuSection();
const isInformationRetrievalSettings = useIsInformationRetrievalSettings();

const menuComponent = computed(() => {
  if (!section.value) return undefined;
  if (isInformationRetrievalSettings.value) {
    return InformationRetrievalSettingsMenu;
  }
  if (featureFlags.ANDEV_10131_CAPABILITY && section.value === 'my_page') {
    return CapabilityMyPageMenu;
  }
  return MENU_BY_SECTION[section.value];
});

// マイページと個人の設定は同じセクションなので、セクションだけではKeepAliveが切り替わらない
const menuKey = computed(() =>
  isInformationRetrievalSettings.value
    ? 'information_retrieval_settings'
    : section.value,
);
</script>

<template>
  <KeepAlive>
    <component :is="menuComponent" :key="menuKey" v-if="menuComponent" />
  </KeepAlive>
</template>
